import React, { useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { useNavigate } from "react-router-dom";
import { ConsumerOnboarding } from "../../types";
import { useConsumerList, useConsumerOnboardingExport } from "../../hooks";
import { ConsumerOnboardingFilterPanel } from "../filters";

const columns: TableColumn<ConsumerOnboarding>[] = [
  { key: "consumerNo", header: "Consumer No", type: "text" },
  {
    key: "firstName",
    header: "Applicant",
    type: "custom",
    render: (_: any, row: any) => (
      <div>
        <div className="font-semibold">
          {row.firstName} {row.lastName}
        </div>
        <div className="text-xs text-muted-foreground">{row.email}</div>
      </div>
    ),
  },
  { key: "createdDate", header: "Submission Date", type: "text" },
  {
    key: "statusDisplay",
    header: "Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) =>
        value === "DRAFT STATE" ? "destructive" : "default",
    },
  },
  { key: "categoryDisplay", header: "Account Category", type: "text" },
  { key: "subCategoryDisplay", header: "Account Sub Category", type: "text" },
];

export const CurrentList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  // Function to get the appropriate route based on status
  const getRouteByStatus = (row: ConsumerOnboarding) => {
    const status = row.statusDisplay.toUpperCase();

    switch (status) {
      case "PENDING":
        return `/cx/consumer-onboarding/view/${row.id}/${row.statusDisplay}`;
      case "ACCEPTED":
        return `/cx/consumer-onboarding/workflow/inspection/${row.id}/${row.statusDisplay}`;
      case "SURVEY INPROGRESS":
        return `/cx/consumer-onboarding/workflow/approval/${row.id}/${row.statusDisplay}`;
      case "SURVEY COMPLETED":
        return `/cx/consumer-onboarding/workflow/approval/${row.id}/${row.statusDisplay}`;

      case "APPROVED":
        return `/cx/consumer-onboarding/workflow/installation/${row.id}/${row.statusDisplay}`;

      case "INSTALLATION INPROGRESS":
        return `/cx/consumer-onboarding/workflow/installation/${row.id}/${row.statusDisplay}`;
      case "INSTALLATION COMPLETED":
        return `/cx/consumer-onboarding/workflow/activation/${row.id}/${row.statusDisplay}`;
      // Fallback to application review for any other status
      default:
        return `/cx/consumer-onboarding/view/${row.id}/${row.statusDisplay}`;
    }
  };

  const actions: TableAction<ConsumerOnboarding>[] = [
    {
      label: "View Details",
      onClick: (row) => navigate(getRouteByStatus(row)),
      hidden: (row) =>
        row.statusDisplay === "DRAFT STATE" ||
        row.statusDisplay === "PAYMENT PENDING",
    },
    {
      label: "Record Pending Payment",
      onClick: (row) =>
        navigate(`/cx/consumer-management/manual/success/${row.id}`, {
          state: {
            planSelection: row.remotePlanId,
            consumerNo: row.consumerNo,
          },
        }),
      hidden: (row) => row.statusDisplay !== "PAYMENT PENDING",
    },
    {
  label: "Edit Application",
  onClick: (row) => {
    // 🔄 Clear localStorage key
    localStorage.removeItem("consumerFirstStepData");
    navigate(`/cx/consumer-management/manual/edit/${row.id}`);
  },
  hidden: (row) => row.statusDisplay !== "DRAFT STATE",
}
  ];

  // Memoize the configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
        name: "name",
      },
      defaults: {
        page: DEFAULT_PAGE_NUMBER,
        limit: PAGE_SIZE,
        status: '',
        category: "ALL_CATEGORIES",
        search: "",
        name: "name",
      },
      parsers: {
        status: (value: string) => value,
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: string) => value,
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const { data } = useConsumerList({
    page: filters.page,
    limit: PAGE_SIZE,
    searchData: filters.search_data,
    status: filters.status|| "",
    remoteUtilityId: Number(remoteUtilityId),
  });

  // Transform API data to match the table structure
  const transformedData: ConsumerOnboarding[] =
    data?.results?.map((item: ConsumerOnboarding) => ({
      id: item.id,
      email: item.email,
      category: item.category,
      consumerNo: item.consumerNo,
      status: item.status,
      statusDisplay: item.statusDisplay,
      createdDate: item.createdDate,
      firstName: item.firstName,
      lastName: item.lastName,
      subCategory: item.subCategory,
      categoryDisplay: item.categoryDisplay,
      subCategoryDisplay: item.subCategoryDisplay,
      remotePlanId: item.remotePlanId,
      progressStatusDisplay: item.progressStatusDisplay,
    })) || [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const exportParams = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      page: filters.page,
      limit: PAGE_SIZE,
      searchData: filters.search_data,
      status: filters.status || "",
    };
  }, [filters, remoteUtilityId]);

  const { exportCSV, isExporting } = useConsumerOnboardingExport(exportParams);

  const handleExport = () => {
    exportCSV();
  };

  return (
    <>
      <GenericTable
        data={transformedData}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by name, email, or ID..."
        searchParamName="query"
        searchDebounceMs={300}
        onExport={handleExport}
        exportLoading={isExporting}
        onSearch={handleSearch}
        pagination={{
          currentPage: page,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem: (page - 1) * PAGE_SIZE + 1,
          endItem: Math.min(page * PAGE_SIZE, totalItems),
        }}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
        filterPanel={
          <ConsumerOnboardingFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
      />
    </>
  );
};
